import { createFileRoute, Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { Fragment } from 'react';

import { PhoebeAsksFirstSection } from '@apps/web/components/settings/phoebe_asks_first_section';
import { PhoebeAutomationSections } from '@apps/web/components/settings/phoebe_automation_sections';
import { Alert, AlertDescription } from '@apps/web/components/ui/alert';
import {
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/settings/phoebe')({
    component: PhoebePage,
});

function PhoebePage() {
    // No permission check: every endpoint this page calls admits any member of
    // the organization, so anyone who can reach it can already use it.
    const { organizationId, isLoading, gates } = useSettingsGates();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });
    const calloutsNotificationUiEnabled = useFeatureFlag(
        'ete_callouts_notification_ui',
        organization?.features,
    );
    const phoebeWorkflowsEnabled = useFeatureFlag(
        'phoebe_end_to_end_outreach_enabled',
        organization?.features,
    );
    const agentOutreachDisabled =
        organization?.callout_settings?.agent_outreach_enabled === false;
    if (isLoading) {
        return null;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <Fragment>
            {agentOutreachDisabled || !phoebeWorkflowsEnabled ? (
                <div className="mx-auto -mb-4 w-full max-w-4xl px-10 pt-12">
                    <div className="space-y-3">
                        {agentOutreachDisabled ? (
                            <Alert
                                variant="destructive"
                                className="border-destructive/40 bg-destructive/5"
                            >
                                <TriangleAlert aria-hidden="true" />
                                <AlertDescription>
                                    <p>
                                        Agent-created outreach is off. Phoebe
                                        cannot create outreaches until it is
                                        restored.
                                    </p>
                                    {gates.canManageOrgSettings ? (
                                        <p>
                                            <Link
                                                to="/settings/outreach-automation"
                                                className="font-medium underline underline-offset-4"
                                            >
                                                Restore agent-created outreach
                                                in Scheduling settings
                                            </Link>
                                            .
                                        </p>
                                    ) : (
                                        <p>
                                            Ask an account admin to restore
                                            agent-created outreach.
                                        </p>
                                    )}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {!phoebeWorkflowsEnabled ? (
                            <Alert className="border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                                <TriangleAlert aria-hidden="true" />
                                <AlertDescription className="text-amber-900 dark:text-amber-200">
                                    <p>
                                        Contact customer experience to discuss
                                        enabling these workflows.
                                    </p>
                                </AlertDescription>
                            </Alert>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <SettingsPageShell
                title="Phoebe"
                description="The work Phoebe takes on autonomously"
            >
                <SettingsPageSections>
                    <PhoebeAutomationSections
                        organizationId={organizationId}
                        toggleEnabled={phoebeWorkflowsEnabled}
                    />
                    {calloutsNotificationUiEnabled ? (
                        <PhoebeAsksFirstSection
                            organizationId={organizationId}
                        />
                    ) : null}
                </SettingsPageSections>
            </SettingsPageShell>
        </Fragment>
    );
}
